import { useState } from 'react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import type { AxiosError } from 'axios';
import { RefundRequestCreatedDialog } from './components/refund-request-created-dialod';
import { mapRefundRequestToFormData } from './utils/transform-json-to-multipart';
import { useNotification } from '@/context/notification';
import { useWizard } from '@/context/wizard';
import {
  useCreateRefundRequest,
  useUpdateRefundRequest,
} from '@/data/subsidy/refund-requests';
import { useRequestRefundableAmount } from '@/data/subsidy/resource-approvals/refundable-amount';
import { RequestLimitDialog } from '@/packages/subsidy/pages/refund-request/components/request-limit-dialog';
import { SummaryBox } from '@/packages/subsidy/pages/refund-request/components/summary-box';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';
import {
  InvoiceWizardStepIds,
  type InvoiceWizardProps,
} from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/interfaces';
import { captureException } from '@/utils/capture-exception';
import { refundValueDetail } from '@/utils/refund-value-detail';

const requestLimitReachedCode = 'request_limit_reached';

export const Summary = () => {
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [showRequestLimitReachedDialog, setShowRequestLimitReachedDialog] =
    useState(false);
  const { toast } = useNotification();
  const { resourceApproval } = useRefundRequest();
  const { values, actions } = useWizard<InvoiceWizardProps>();
  const formValues = values.data;
  const { mutate: createRefundRequest, isPending } = useCreateRefundRequest();
  const { mutate: updateRefundRequest, isPending: isUpdating } =
    useUpdateRefundRequest();
  const { requestRefundableAmount, isFetching: isFetchingRefundableAmount } =
    useRequestRefundableAmount({
      refund_amount_requested: formValues?.payment_amount,
      resource_approval_uuid: resourceApproval?.uuid || '',
      refund_request_uuid: resourceApproval?.refund_request?.uuid || '',
    });
  const selectedPolicyLimits =
    requestRefundableAmount?.policy_limits ??
    resourceApproval?.policies[0]?.limits;
  const { policyText } = refundValueDetail(selectedPolicyLimits);

  const buildFormData = () =>
    mapRefundRequestToFormData({
      data: values.data,
      resourceApprovalUuid: resourceApproval?.uuid || '',
      refundRequestUuid: resourceApproval?.refund_request?.uuid,
    });

  const isLimitReachedError = (error: Error) => {
    return (
      (error as AxiosError<{ code: string }>)?.response?.data?.code ===
      requestLimitReachedCode
    );
  };

  const getFileContext = () => {
    const describeFiles = (files?: File[]) =>
      files?.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })) ?? [];

    return {
      purchase_attachments: describeFiles(values.data?.purchase_attachments),
      payment_attachments: describeFiles(values.data?.payment_attachments),
      optional_files: describeFiles(values.data?.optional_files),
    };
  };

  const handleMutationError = (error: Error, action: string) => {
    captureException({
      error,
      context: {
        files: JSON.stringify(getFileContext()),
      },
    });
    if (isLimitReachedError(error)) {
      return setShowRequestLimitReachedDialog(true);
    }
    toast({
      content: `Erro ao ${action} o pedido de reembolso`,
      type: 'error',
    });
  };

  const editRefund = () => {
    updateRefundRequest(
      {
        formData: buildFormData(),
        id: resourceApproval?.refund_request?.uuid,
      },
      {
        onSuccess: () => {
          toast({
            content: 'Pedido de reembolso editado com sucesso',
            type: 'success',
          });
          setOpenSuccessDialog(true);
        },
        onError: (error) => handleMutationError(error, 'editar'),
      }
    );
  };

  const createRefund = () => {
    createRefundRequest(
      { formData: buildFormData() },
      {
        onSuccess: () => setOpenSuccessDialog(true),
        onError: (error) => handleMutationError(error, 'enviar'),
      }
    );
  };

  const createOrEditRefundRequest = () => {
    if (!resourceApproval) return;

    if (resourceApproval?.refund_request?.uuid) {
      editRefund();
      return;
    }

    createRefund();
  };
  console.log(resourceApproval?.policy_refund_limit);
  if (isFetchingRefundableAmount)
    return <Skeleton height={100} width="100%" variant="rounded" />;

  const summaryItems = [
    { label: 'Valor pago', value: formValues?.payment_amount || '' },
    {
      label: 'Limite de reembolso',
      value: resourceApproval?.policy_refund_limit ?? '',
      detail: policyText,
    },
    {
      label: 'Saldo disponível',
      value: selectedPolicyLimits?.amount_available ?? '',
    },
  ].filter((item) => item.value !== '');

  return (
    <Box>
      <Typography variant="h3">Cálculo do reembolso</Typography>
      <SummaryBox mt={3.5} items={summaryItems} />
      <SummaryBox
        sx={({ palette }) => ({
          border: `1px solid ${palette.divider}`,
          backgroundColor: palette.neutral[100],
        })}
        items={[
          {
            label: 'Reembolso',
            value:
              requestRefundableAmount?.refund_request?.refundable_amount ?? '',
          },
        ]}
      />
      <Stack gap={2} direction="row" justifyContent="flex-end" mt={3.5}>
        <Button
          onClick={() => actions.navigate(InvoiceWizardStepIds.InvoiceForm)}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={createOrEditRefundRequest}
          loading={isPending || isUpdating}
        >
          Enviar
        </Button>
      </Stack>
      {openSuccessDialog && (
        <RefundRequestCreatedDialog
          onClose={() => setOpenSuccessDialog(false)}
        />
      )}
      {showRequestLimitReachedDialog && (
        <RequestLimitDialog
          onClose={() => setShowRequestLimitReachedDialog(false)}
          maxRefundsPerPeriod={
            resourceApproval?.policies?.[0]?.request_limit_info
              ?.max_refunds_per_period
          }
          renewalPeriodInMonths={
            resourceApproval?.policies?.[0]?.request_limit_info
              ?.renewal_period_in_months
          }
        />
      )}
    </Box>
  );
};
